<script setup lang="ts">
import {toRef} from 'vue'
import * as d3 from 'd3'
import type {ChartData} from '@/client/mentor/MessagesClient'
import AssistantMessageChartTooltip from '@/components/assistant/chat/message/AssistantMessageChartTooltip.vue'
import {useAssistantMessageChartLifecycle} from '@/components/assistant/composables/useAssistantMessageChartLifecycle'
import {useAssistantMessageChart} from '@/components/assistant/composables/useAssistantMessageChart'

interface Props {
  chartData: ChartData
}

const props = defineProps<Props>()
const chartData = toRef(props, 'chartData')

const {
  chartTheme,
  chartContainer,
  hiddenLegendItems,
  hoverTooltip,
  pieLegendItems,
  attachTooltipHandlers,
  hideTooltip,
  formatTooltipValue,
  getLegendHeight,
  renderLegendInSvg,
} = useAssistantMessageChart(chartData)

useAssistantMessageChartLifecycle(chartContainer, chartData, hiddenLegendItems, drawChart)

function drawChart() {
  if (!chartContainer.value) {
    return
  }

  const container = chartContainer.value
  const width = container.clientWidth || 320
  const margin = {top: 32, right: 24, bottom: 56, left: 72}
  const chartHeight = 260
  const legendHeight = getLegendHeight(pieLegendItems.value, width - margin.left - margin.right)
  const height = chartHeight + margin.top + margin.bottom + legendHeight

  d3.select(container).selectAll('*').remove()
  hideTooltip()

  if (!pieLegendItems.value.length) {
    return
  }

  const svg = d3
    .select(container)
    .append('svg')
    .attr('width', width)
    .attr('height', height)
    .attr('viewBox', `0 0 ${width} ${height}`)
    .attr('class', 'overflow-visible')

  const radius = Math.min(width - margin.left - margin.right, height - margin.top - margin.bottom) / 2
  const chartGroup = svg.append('g').attr('transform', `translate(${width / 2}, ${margin.top + radius})`)

  const pieData = d3.pie<{label: string; value: number}>().value((item) => item.value)(
    pieLegendItems.value.filter((item) => !item.isHidden)
  )

  const units = Array.from(
    new Set(chartData.value.series.map((series) => series.unit).filter((unit): unit is string => Boolean(unit)))
  )
  const tooltipUnit = units.length === 1 ? units[0] : undefined

  const arcGenerator = d3
    .arc<d3.PieArcDatum<{label: string; value: number}>>()
    .innerRadius(radius * 0.45)
    .outerRadius(radius)

  const slices = chartGroup
    .append('g')
    .selectAll('path')
    .data(pieData)
    .join('path')
    .attr('d', arcGenerator)
    .attr(
      'fill',
      (item) =>
        pieLegendItems.value.find((legendItem) => legendItem.label === item.data.label)?.color ?? chartTheme.seriesFallbackColor
    )
    .attr('stroke', chartTheme.pieSliceStrokeColor)
    .attr('stroke-width', 2)

  attachTooltipHandlers(slices, (item) => ({
    title: item.data.label,
    value: formatTooltipValue(item.data.value, tooltipUnit),
  }))

  chartGroup
    .append('text')
    .attr('text-anchor', 'middle')
    .attr('y', -2)
    .attr('fill', chartTheme.pieCenterTextColor)
    .attr('font-size', 18)
    .attr('font-weight', 700)
    .text(formatTooltipValue(d3.sum(pieLegendItems.value, (item) => item.value)))

  renderLegendInSvg(svg, pieLegendItems.value, margin, 'pie', false, legendHeight)
}
</script>

<template>
  <div class="relative w-full rounded-xl border" :class="chartTheme.containerClass">
    <div ref="chartContainer" class="w-full overflow-hidden"></div>

    <AssistantMessageChartTooltip
      :container-class="chartTheme.tooltipContainerClass"
      :title-class="chartTheme.tooltipTitleClass"
      :tooltip="hoverTooltip"
      :value-class="chartTheme.tooltipValueClass"
    />
  </div>
</template>
